import { MeshLoadCancelledError, throwIfMeshLoadCancelled } from '@/plugins/mesh/errors'
import type { MeshLoadRequest, MeshSourceData } from '@/plugins/mesh/types'
import type { LoadContext } from '@/shared/types/assets'

export type MeshSourceKind = 'memory' | 'local-file' | 'http' | 'blob' | 'data' | 'other-url'

export type MeshSourceReadStrategy = 'memory' | 'array-buffer' | 'preallocated' | 'chunk-copy'

const DEFAULT_MAX_PREALLOCATE_BYTES = 64 * 1024 * 1024

/** 完整 Mesh 响应读取的内部内存约束。 */
export interface MeshSourceReadOptions {
  /** 仅当可信 Content-Length 不超过该值时才按响应头预分配。 */
  readonly maxPreallocateBytes?: number
}

/** 完整 Mesh 来源读取的低频聚合诊断。 */
export interface MeshSourceReadPerformanceReport {
  readonly sourceKind: MeshSourceKind
  readonly strategy: MeshSourceReadStrategy
  readonly responseWaitMs: number
  readonly bodyReadMs: number
  readonly bodyCopyMs: number
  readonly chunkCount: number
  readonly byteLength: number
  readonly estimatedPeakBufferedBytes: number
}

/** 将 URL 查询和 hash 排除后提取小写扩展名。 */
export function getMeshSourceExtension(url: string | undefined): string | undefined {
  if (!url) return undefined
  const pathname = url.split(/[?#]/, 1)[0]
  const match = pathname.match(/\.([^.\\/]+)$/)
  return match?.[1]?.toLowerCase()
}

/** 为错误和诊断生成稳定的来源描述。 */
export function describeMeshSource(request: MeshLoadRequest): string {
  return request.url ?? (request.data === undefined ? '(未提供来源)' : '(内存数据)')
}

/** 同时向 AssetLoader 上下文和请求级回调报告单调进度。 */
export function createMeshProgressReporter(
  request: MeshLoadRequest,
  context: LoadContext
): (progress: number) => void {
  let lastProgress = 0
  return (progress: number) => {
    const next = Math.max(lastProgress, Math.min(1, progress))
    lastProgress = next
    context.onProgress?.(next)
    if (request.onProgress !== context.onProgress) {
      request.onProgress?.(next)
    }
  }
}

function copyViewBuffer(view: ArrayBufferView): ArrayBuffer {
  const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
  return bytes.slice().buffer
}

/** 将调用方直接提供的数据统一为 Three.js Loader 可解析的数据。 */
export async function normalizeMeshSourceData(data: MeshSourceData): Promise<ArrayBuffer | string> {
  if (typeof data === 'string' || data instanceof ArrayBuffer) return data
  if (ArrayBuffer.isView(data)) return copyViewBuffer(data)
  return data.arrayBuffer()
}

async function readResponseWithProgress(
  response: Response,
  signal: AbortSignal | undefined,
  source: string,
  onProgress: (progress: number) => void,
  responseWaitMs: number,
  sourceKind: MeshSourceKind,
  maxPreallocateBytes: number
): Promise<{ data: ArrayBuffer; performance: MeshSourceReadPerformanceReport }> {
  const reader = response.body?.getReader()
  const declaredLength = Number(response.headers.get('content-length'))
  const total = Number.isSafeInteger(declaredLength) && declaredLength > 0 ? declaredLength : 0
  if (!reader) {
    const bodyStartedAt = performance.now()
    const buffer = await response.arrayBuffer()
    const bodyReadMs = performance.now() - bodyStartedAt
    throwIfMeshLoadCancelled(signal, source)
    onProgress(1)
    return {
      data: buffer,
      performance: {
        sourceKind,
        strategy: 'array-buffer',
        responseWaitMs,
        bodyReadMs,
        bodyCopyMs: 0,
        chunkCount: buffer.byteLength > 0 ? 1 : 0,
        byteLength: buffer.byteLength,
        estimatedPeakBufferedBytes: buffer.byteLength,
      },
    }
  }

  let completed = false
  const bodyStartedAt = performance.now()
  try {
    let target: Uint8Array | undefined
    let preallocatedBytes = 0
    if (total > 0 && total <= maxPreallocateBytes) {
      try {
        target = new Uint8Array(total)
        preallocatedBytes = total
      } catch {
        target = undefined
      }
    }
    let chunks: Uint8Array[] | undefined = target ? undefined : []
    let loaded = 0
    let chunkCount = 0
    let maxChunkBytes = 0
    while (true) {
      throwIfMeshLoadCancelled(signal, source)
      const { done, value } = await reader.read()
      if (done) {
        completed = true
        break
      }
      chunkCount += 1
      maxChunkBytes = Math.max(maxChunkBytes, value.byteLength)
      if (target && loaded + value.byteLength <= target.byteLength) {
        target.set(value, loaded)
      } else {
        if (!chunks) {
          chunks = []
          if (loaded > 0 && target) chunks.push(target.slice(0, loaded))
        }
        chunks.push(value)
        target = undefined
      }
      loaded += value.byteLength
      onProgress(total > 0 ? loaded / total : 0)
    }

    const bodyReadMs = performance.now() - bodyStartedAt
    let bodyCopyMs = 0
    let data: Uint8Array
    let strategy: MeshSourceReadStrategy
    if (target && loaded === target.byteLength) {
      data = target
      strategy = 'preallocated'
    } else {
      const copyStartedAt = performance.now()
      if (target) {
        data = target.slice(0, loaded)
      } else {
        data = new Uint8Array(loaded)
        let offset = 0
        for (const chunk of chunks ?? []) {
          data.set(chunk, offset)
          offset += chunk.byteLength
        }
      }
      bodyCopyMs = performance.now() - copyStartedAt
      strategy = 'chunk-copy'
    }
    onProgress(1)
    return {
      data: data.buffer as ArrayBuffer,
      performance: {
        sourceKind,
        strategy,
        responseWaitMs,
        bodyReadMs,
        bodyCopyMs,
        chunkCount,
        byteLength: loaded,
        estimatedPeakBufferedBytes:
          strategy === 'preallocated'
            ? loaded + maxChunkBytes
            : Math.max(loaded * 2, preallocatedBytes + loaded),
      },
    }
  } catch (error) {
    if (signal?.aborted) {
      throw new MeshLoadCancelledError(source, { cause: signal.reason ?? error })
    }
    throw error
  } finally {
    if (!completed) {
      try {
        await reader.cancel(signal?.reason)
      } catch {
        // Fetch 取消可能已经使 reader 进入错误状态。
      }
    }
  }
}

function inferMeshSourceKind(response: Response, source: string): MeshSourceKind {
  if (response.headers.get('x-share3d-source-kind') === 'local-file') return 'local-file'
  try {
    const protocol = new URL(response.url || source, globalThis.location?.href).protocol
    if (protocol === 'http:' || protocol === 'https:') return 'http'
    if (protocol === 'blob:') return 'blob'
    if (protocol === 'data:') return 'data'
  } catch {
    // 非标准 URL 按其他来源聚合。
  }
  return 'other-url'
}

function getSourceDataByteLength(data: ArrayBuffer | string): number {
  return typeof data === 'string' ? new TextEncoder().encode(data).byteLength : data.byteLength
}

/** 获取主资产数据，支持内存来源、Fetch 取消和下载进度。 */
export async function loadMeshSourceData(
  request: MeshLoadRequest,
  context: LoadContext,
  onProgress: (progress: number) => void,
  onPerformance?: (report: MeshSourceReadPerformanceReport) => void,
  options: MeshSourceReadOptions = {}
): Promise<ArrayBuffer | string> {
  const source = describeMeshSource(request)
  const signal = context.signal ?? request.signal
  throwIfMeshLoadCancelled(signal, source)

  if (request.data !== undefined) {
    const bodyStartedAt = performance.now()
    const data = await normalizeMeshSourceData(request.data)
    const bodyReadMs = performance.now() - bodyStartedAt
    throwIfMeshLoadCancelled(signal, source)
    onProgress(1)
    if (onPerformance) {
      const byteLength = getSourceDataByteLength(data)
      onPerformance({
        sourceKind: 'memory',
        strategy: 'memory',
        responseWaitMs: 0,
        bodyReadMs,
        bodyCopyMs: 0,
        chunkCount: byteLength > 0 ? 1 : 0,
        byteLength,
        estimatedPeakBufferedBytes: byteLength,
      })
    }
    return data
  }

  if (!request.url) {
    throw new Error('MeshLoadRequest 必须提供 url 或 data')
  }

  let response: Response
  const responseStartedAt = performance.now()
  try {
    response = await fetch(request.url, {
      signal,
      headers: request.headers,
      credentials: request.credentials,
    })
  } catch (error) {
    if (signal?.aborted) {
      throw new MeshLoadCancelledError(source, { cause: signal.reason ?? error })
    }
    throw error
  }

  if (!response.ok) {
    throw new Error(`请求失败: ${response.status} ${response.statusText}`)
  }
  const responseWaitMs = performance.now() - responseStartedAt
  const maxPreallocateBytes =
    typeof options.maxPreallocateBytes === 'number' &&
    Number.isFinite(options.maxPreallocateBytes) &&
    options.maxPreallocateBytes >= 0
      ? Math.floor(options.maxPreallocateBytes)
      : DEFAULT_MAX_PREALLOCATE_BYTES
  const result = await readResponseWithProgress(
    response,
    signal,
    source,
    onProgress,
    responseWaitMs,
    inferMeshSourceKind(response, request.url),
    maxPreallocateBytes
  )
  onPerformance?.(result.performance)
  return result.data
}

/** 根据主资产 URL 推导 glTF 外部资源基准路径。 */
export function resolveMeshBaseUrl(request: MeshLoadRequest): string {
  if (request.baseUrl) return request.baseUrl
  if (!request.url) return ''
  try {
    const resolved = new URL(request.url, globalThis.location?.href ?? 'http://localhost/')
    return new URL('.', resolved).href
  } catch {
    const slash = request.url.lastIndexOf('/')
    return slash >= 0 ? request.url.slice(0, slash + 1) : ''
  }
}
